import { Clock, Stack } from "@phosphor-icons/react";

/** Collapsible panel that splits a contact list into 500-batch scheduled
 * child campaigns. Lives inside the Schedule card. */
export default function AutoStaggerPanel({
    open, onToggle,
    start, setStart,
    batchSize, setBatchSize,
    hours, setHours,
    states, toggleState,
    availableStates,
    busy, onSubmit,
    result,
}) {
    return (
        <div className="mt-4 pt-4 border-t border-dashed border-slate-700">
            <button
                type="button"
                onClick={onToggle}
                className="w-full flex items-center justify-between gap-2 text-xs text-orange-300 hover:text-orange-200 font-mono-alt uppercase tracking-wider py-1"
                data-testid="auto-stagger-toggle"
            >
                <span className="flex items-center gap-2">
                    <Stack size={14} weight="fill" />
                    Auto-stagger 500/day batches
                </span>
                <span className="text-[10px]">{open ? "▾" : "▸"}</span>
            </button>
            {open && (
                <div className="mt-3 space-y-3 bg-slate-950/70 border border-slate-800 rounded-lg p-3" data-testid="auto-stagger-panel">
                    <p className="text-[11px] text-slate-400 leading-relaxed">
                        Splits your active contacts into <span className="text-cyan-300 font-mono-alt">500-batch</span> child
                        campaigns and schedules each one <span className="text-orange-300 font-mono-alt">N hours apart</span>.
                        Respects Gmail's 500/day limit while reaching the entire list.
                    </p>

                    <div>
                        <label className="overline block mb-1">Start at (UTC)</label>
                        <input
                            type="datetime-local"
                            value={start}
                            onChange={(e) => setStart(e.target.value)}
                            className="w-full bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-xs text-slate-100"
                            data-testid="stagger-start"
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <label className="overline block mb-1">Batch size</label>
                            <input
                                type="number" min={50} max={500} step={50}
                                value={batchSize}
                                onChange={(e) => setBatchSize(Math.max(50, Math.min(500, Number(e.target.value) || 500)))}
                                className="w-full bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-xs text-slate-100 font-mono-alt"
                                data-testid="stagger-batch-size"
                            />
                        </div>
                        <div>
                            <label className="overline block mb-1">Hours apart</label>
                            <input
                                type="number" min={1} max={168} step={1}
                                value={hours}
                                onChange={(e) => setHours(Math.max(1, Math.min(168, Number(e.target.value) || 24)))}
                                className="w-full bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-xs text-slate-100 font-mono-alt"
                                data-testid="stagger-hours"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="overline block mb-1">Filter by states (optional)</label>
                        <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto">
                            {availableStates.length === 0 && (
                                <span className="text-[10px] text-slate-500">No states yet — add contacts first.</span>
                            )}
                            {availableStates.map((st) => {
                                const active = states.includes(st);
                                return (
                                    <button
                                        key={st}
                                        type="button"
                                        onClick={() => toggleState(st)}
                                        className={`px-2 py-0.5 rounded text-[10px] font-mono-alt border ${
                                            active
                                                ? "bg-orange-500/20 border-orange-400/60 text-orange-100"
                                                : "bg-slate-900 border-slate-800 text-slate-400 hover:text-slate-200"
                                        }`}
                                        data-testid={`stagger-state-${st}`}
                                    >
                                        {st}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <button
                        type="button"
                        onClick={onSubmit}
                        disabled={busy || !start}
                        className="btn-primary !py-2 w-full justify-center text-xs disabled:opacity-50"
                        data-testid="stagger-submit"
                    >
                        <Clock size={14} weight="bold" />
                        {busy ? "Scheduling…" : "Schedule batches"}
                    </button>

                    {result && (
                        <div className="text-[10px] text-slate-300 font-mono-alt bg-slate-900/80 border border-emerald-500/30 rounded p-2 leading-relaxed" data-testid="stagger-result">
                            ✓ <span className="text-emerald-300">{result.batches_created}</span> campaigns created<br />
                            Total recipients: <span className="text-cyan-300">{result.total_recipients}</span><br />
                            First send: <span className="text-cyan-300">{new Date(result.first_send).toLocaleString()}</span><br />
                            Last send: <span className="text-cyan-300">{new Date(result.last_send).toLocaleString()}</span>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
